import { useState, useRef, useEffect } from 'react';
import { Slide } from '@mui/material';
import TWEEN from '@tweenjs/tween.js';
import { usePreloadCelebration } from './CelebrationHooks';
import { celebrationsMap } from './CelebrationMapping';
import './Celebration.scss';
import Logger from '../../../utils/Logger';
import { useDisableRightClick } from '../TerminologiesHooks';
import LoadingPlaceHolder from '../LoadingPlaceHolder';
import { filterUndefined } from '../../../utils/FilterUndefinedObject';

const defaultProps = {
  show: false,
  onFinish: () => {},
  onOpen: () => {},
  enableAudio: false,
};

const Celebration = (props) => {
  const mergedProps = { ...defaultProps, ...filterUndefined(props) };
  const { celebrationData, onFinish, onOpen, show, enableAudio } = mergedProps;
  const [ready, setReady] = useState(false);
  const celebrationList = useRef([celebrationData]);
  const pixiLoader = usePreloadCelebration(celebrationList.current, enableAudio);
  const CelebrationComponent = celebrationsMap[celebrationData.id];

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useDisableRightClick(ready && show);
  useEffect(() => (() => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
  }), []);

  useEffect(() => {
    if (show && onOpen) {
      onOpen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (pixiLoader && intervalRef.current == null) {
    intervalRef.current = setInterval(() => {
      let resources = Object.keys(pixiLoader.resources).filter((key) => key.startsWith(celebrationData.id));
      if (!enableAudio) {
        resources = resources.filter((r) => {
          const resource = pixiLoader.resources[r];
          return !(['mp3'].includes(resource.extension) || (resource && resource.data && resource.data.nodeName && resource.data.nodeName.toLowerCase() === 'audio'));
        });
      }
      const notCompleted = resources.length > 0 && resources.findIndex((r) => (pixiLoader.resources[r].isLoading));
      if (notCompleted < 0) {
        clearInterval(intervalRef.current);
        clearTimeout(timeoutRef.current);
        setReady(true);
      }
    }, 100);
  }

  // Setup the animation loop.
  function animate(time) {
    requestAnimationFrame(animate);
    TWEEN.update(time);
  }
  requestAnimationFrame(animate);

  if (!show) {
    return null;
  }

  // if the resurces cannot be loaded within 30 seconds, then skip the celebration
  if (!timeoutRef.current) {
    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
      if (!ready) {
        Logger.logError(`Unable to load celebration ${celebrationData.id}`);
        onFinish();
      }
    }, 30000);
  }

  return (
    <div className='celebration-container'>
      {ready ? (
        <Slide in direction='left' timeout={500}>
          <div className='celebration' data-test='celebration-container'>
            <CelebrationComponent
              pixiLoader={pixiLoader}
              celebrationData={celebrationData}
              onFinish={onFinish}
            />
          </div>
        </Slide>
      ) : (
        <LoadingPlaceHolder message='Celebration is loading.' />
      )}
    </div>
  );
};

export default Celebration;
